<template>
  <b-tabs>
    <b-tab v-if="$can('update', 'user')" active title="General">
      <b-form
        v-if="initialised"
        autocomplete="chrome-off"
        enctype="multipart/form-data"
        @keydown="form.errors.clear($event.target.id)"
        @submit.prevent="onSubmit"
      >
        <div class="row">
          <div class="col-8">
            <div class="row">
              <div class="col">
                <b-card class="profile-card">
                  <template #header>
                    Profile Photo

                    <div
                      v-if="form.profile_photo_time && form.profile_photo_time.length > 0"
                      class="float-right"
                    >
                      <small class="text-muted pr-1">Updated on</small>
                      <b-badge pill variant="secondary">
                        {{ formatLocalDate(form.profile_photo_time, "DD MMM YYYY") }}
                      </b-badge>
                    </div>
                  </template>
                  <file-uploader
                    :file-url="form.profile_photo"
                    :image-placeholder="profilePlaceholder"
                    :image-preview="true"
                    aspect-ratio="1"
                    @uploaded="setProfilePhoto"
                  />
                </b-card>
              </div>
              <div class="col">
                <b-card class="van-card">
                  <template #header>
                    Van Photo
                    <div
                      v-if="form.van_photo_time && form.van_photo_time.length > 0"
                      class="float-right"
                    >
                      <small class="text-muted pr-1">Updated on</small>
                      <b-badge pill variant="secondary">
                        {{ formatLocalDate(form.van_photo_time, "DD MMM YYYY") }}
                      </b-badge>
                    </div>
                  </template>
                  <file-uploader
                    :file-url="form.van_photo"
                    :image-preview="true"
                    @uploaded="setVanPhoto"
                  />
                </b-card>
              </div>
            </div>

            <b-card class="user-general-card">
              <template #header>
                <strong>User</strong>
                General

                <div v-if="crud.getAction() === 'edit'" class="float-right">
                  Created by
                  <b-badge pill variant="primary">
                    {{ crud.data.registered_by ? crud.data.registered_by.name : "No One" }}
                  </b-badge>
                </div>
              </template>

              <form-bootstrap-radio
                v-if="$can('update', crud, 'roles')"
                v-model="form.role"
                :options="['superadmin', 'admin', 'manager', 'technician']"
                name="role"
              />
              <form-text v-else v-model="form.role" :disabled="true" name="role" />

              <form-bootstrap-radio v-model="form.status" :options="statuses" name="status">
                <template #note>
                  <div v-if="crud.data.status === 'Inactive'">
                    <small class="text-muted">
                      Inactive at
                      {{ formatLocalDate(crud.data.inactive_at, "DD MMM YYYY@hh:mma") }}
                    </small>
                  </div>
                </template>
              </form-bootstrap-radio>

              <div
                :class="{ 'invalid-wrapper': form.errors.has('has_webui_access') }"
                class="form-group row"
              >
                <div class="col-3 col-form-label text-right">Can Access WebUI</div>
                <div class="col-8">
                  <core-switch
                    v-model="form.has_webui_access"
                    color="primary"
                    off="N"
                    on="Y"
                    variant="3d"
                    @change="form.errors.clear('has_webui_access')"
                  />

                  <small
                    v-if="form.errors.has('has_webui_access')"
                    class="invalid-feedback"
                    v-text="form.errors.get('has_webui_access')"
                  ></small>
                </div>
              </div>

              <div
                :class="{ 'invalid-wrapper': form.errors.has('core_compliance') }"
                class="form-group row"
              >
                <div class="col-3 col-form-label text-right">Core Compliance Passed</div>
                <div class="col-8">
                  <core-switch
                    v-model="form.core_compliance"
                    color="primary"
                    off="N"
                    on="Y"
                    variant="3d"
                    @change="form.errors.clear('core_compliance')"
                  />

                  <small
                    v-if="form.errors.has('core_compliance')"
                    class="invalid-feedback"
                    v-text="form.errors.get('core_compliance')"
                  ></small>
                </div>
              </div>
              <form-user-core-compliance-violations-select
                v-if="$can('update', 'user')"
                v-show="!form.core_compliance"
                v-model="form.core_compliance_violations"
                name="core_compliance_violations"
              />

              <form-text v-model="form.first_name" name="first_name" />
              <form-text v-model="form.last_name" name="last_name" />
              <form-email v-model="form.email" name="email" required />
              <form-text v-model="form.phone" name="phone" />
              <form-text v-model="form.mobile" name="mobile" />
              <form-date
                v-model="form.date_of_birth"
                name="date_of_birth"
                format="dd/MM/yyyy"
                value-format="yyyy-MM-dd"
              />

              <template v-if="form.type !== 'Employee'">
                <form-text v-model="form.company_name" name="company_name"></form-text>
                <form-text v-model="form.abn" name="abn">ABN</form-text>
              </template>

              <form-password v-model="form.password" :autocomplete="false" name="password">
                <template #note>
                  <small v-if="crud.getAction() !== 'create'" class="text-muted"
                    >Leave empty to keep current password.</small
                  >
                </template>
              </form-password>
              <form-text
                v-model="form.professional_accreditation_number"
                name="professional_accreditation_number"
              />
              <form-text v-model="form.driver_license" name="driver_license" />
              <form-text v-model="form.vehicle_registration" name="vehicle_registration" />
              <form-textarea v-model="form.comments" name="comments" />
              <form-address
                v-if="form.addresses[0]"
                v-model="form.addresses[0].id"
                :address="form.addresses[0]"
                :addressable-id="crud.data.id"
                :replaceable="true"
                addressable-type="user"
                name="address"
                @change="form.addresses[0].id = null"
                @updateAddress="form.addresses[0] = $event"
              />
            </b-card>
          </div>

          <div class="col-4">
            <template v-if="$can('update', crud, 'brand-assignments')">
              <div
                v-show="
                  form.role === 'admin' || form.role === 'manager' || form.role === 'technician'
                "
              >
                <b-card>
                  <template #header>Brand Assignments</template>
                  <div
                    v-for="brand in allBrandData"
                    :key="brand.id"
                    :class="[
                      'custom-control',
                      form.role === 'technician' ? 'custom-radio' : 'custom-checkbox',
                    ]"
                  >
                    <input
                      v-if="form.role === 'technician'"
                      :id="`brand-${brand.id}`"
                      v-model="form.brand_single"
                      :value="brand.id"
                      class="custom-control-input"
                      type="radio"
                      @change="updateFormBrands"
                    />
                    <input
                      v-else
                      :id="`brand-${brand.id}`"
                      v-model="form.brands"
                      :value="brand.id"
                      class="custom-control-input"
                      type="checkbox"
                    />
                    <label :for="`brand-${brand.id}`" class="custom-control-label">{{
                      brand.name
                    }}</label>
                  </div>
                </b-card>
              </div>
            </template>

            <div v-show="form.role === 'manager'">
              <b-card>
                <template #header>Supervisees</template>
                <div>
                  <form-user-select
                    v-model="form.supervisees"
                    :labelless="true"
                    :multiple="true"
                    name="supervisees"
                    placeholder="Search technician..."
                    @change="updateSupervisees"
                  />
                </div>
              </b-card>
            </div>

            <div v-show="form.role === 'technician'">
              <b-card>
                <template #header>Service Types</template>
                <div>
                  <form-service-types-select
                    v-model="form.service_types"
                    :disabled="selectedBrandIds.length === 0"
                    :filter-by-brand-ids="selectedBrandIds"
                    :group-select="true"
                    :labelless="true"
                    name="service_types"
                  />
                </div>
              </b-card>

              <b-card>
                <template #header>Google My Business</template>
                <div class="row">
                  <div class="col">
                    <form-text
                      v-model.trim="form.gmb_listing_url"
                      name="gmb_listing_url"
                      :single-row="false"
                      label-position="left"
                    >
                      GMB Listing URL
                    </form-text>
                  </div>
                </div>
              </b-card>

              <template v-if="$can('update', crud, 'types')">
                <b-card no-body>
                  <template #header>Technician Type</template>
                  <b-card-body>
                    <form-select
                      v-model="form.type"
                      :labelless="true"
                      :options="['Employee', 'Contractor', 'Franchisee']"
                      name="type"
                      bottom-margin=""
                    />
                  </b-card-body>

                  <div>
                    <div class="card-header">Start Date</div>

                    <b-card-body>
                      <form-date
                        v-model="form.start_date"
                        name="start_date"
                        format="dd/MM/yyyy"
                        value-format="yyyy-MM-dd"
                        :labelless="true"
                        :bottom-margin="form.type === 'Employee' ? 'mb-3' : ''"
                      />
                    </b-card-body>
                  </div>

                  <div v-if="form.status === 'Inactive'">
                    <div class="card-header">Inactive Reason</div>

                    <b-card-body>
                      <form-text
                        v-model.trim="form.inactive_reason"
                        name="inactive_reason"
                        :single-row="false"
                        :labelless="true"
                      />
                    </b-card-body>
                  </div>

                  <template v-if="form.type === 'Contractor'">
                    <div class="card-header">Profit Sharing</div>

                    <b-card-body>
                      <div class="row">
                        <div class="col">
                          <form-text
                            v-model.trim="form.contractor_job_commission"
                            :is-number="true"
                            :single-row="false"
                            label-position="left"
                            max="100"
                            min="0"
                            name="contractor_job_commission"
                            step="0.1"
                          >
                            Job Commission
                            <template #append>
                              <span class="input-group-text">%</span>
                            </template>
                          </form-text>
                        </div>
                        <div class="col">
                          <form-text
                            v-model.trim="form.contractor_booking_commission"
                            :is-number="true"
                            :single-row="false"
                            label-position="left"
                            max="100"
                            min="0"
                            name="contractor_booking_commission"
                            step="0.1"
                          >
                            Booking Commission
                            <template #append>
                              <span class="input-group-text">%</span>
                            </template>
                          </form-text>
                        </div>
                      </div>
                    </b-card-body>
                  </template>

                  <template v-if="form.type === 'Franchisee'">
                    <div class="card-header">Commission</div>

                    <b-card-body>
                      <div class="row">
                        <div class="col">
                          <form-text
                            v-model.trim="form.franchisee_job_commission"
                            :is-number="true"
                            :single-row="false"
                            label-position="left"
                            max="100"
                            min="0"
                            name="franchisee_job_commission"
                            step="0.1"
                          >
                            Job Commission
                            <template #append>
                              <span class="input-group-text">%</span>
                            </template>
                          </form-text>
                        </div>
                        <div class="col">
                          <form-text
                            v-model.trim="form.franchisee_booking_commission"
                            :is-number="true"
                            :single-row="false"
                            label-position="left"
                            max="100"
                            min="0"
                            name="franchisee_booking_commission"
                            step="0.1"
                          >
                            Booking Commission
                            <template #append>
                              <span class="input-group-text">%</span>
                            </template>
                          </form-text>
                        </div>
                      </div>
                      <div class="row">
                        <div class="col">
                          <form-text
                            v-model.trim="form.admin_fee"
                            :is-number="true"
                            :single-row="false"
                            label-position="left"
                            min="0"
                            name="admin_fee"
                            step="0.01"
                          >
                            Admin Fee
                            <template #prepend>
                              <span class="input-group-text">$</span>
                            </template>
                          </form-text>
                        </div>
                        <div class="col">
                          <form-text
                            v-model.trim="form.marketing_fee"
                            :is-number="true"
                            :single-row="false"
                            label-position="left"
                            min="0"
                            name="marketing_fee"
                            step="0.01"
                          >
                            Marketing Fee
                            <template #prepend>
                              <span class="input-group-text">$</span>
                            </template>
                          </form-text>
                        </div>
                      </div>
                    </b-card-body>
                  </template>
                </b-card>
              </template>
            </div>

            <b-card>
              <template #header>
                <div class="row">
                  <div class="col">Designation</div>
                  <div class="col text-right">
                    <a
                      v-if="crud.data.designation_id"
                      :href="`/designations/chart?user_id=${crud.data.id}`"
                      target="_blank"
                    >
                      <font-awesome-icon :icon="['far', 'eye']" />
                    </a>
                    <div v-else class="text-muted cursor-not-allowed">
                      <font-awesome-icon :icon="['far', 'eye']" />
                    </div>
                  </div>
                </div>
              </template>
              <div>
                <form-designation-select
                  v-model="form.designation"
                  :single-row="false"
                  name="designation"
                  @change="updateDesignation"
                />
              </div>

              <template
                v-if="designationFields && designationFields.length && form.designation_data"
                #footer
              >
                <div class="row">
                  <div v-for="field in designationFields" :key="field.slug" class="col-6">
                    <form-text
                      v-model.trim="form.designation_data[field.slug]"
                      :is-number="true"
                      :single-row="false"
                      :name="field.slug"
                      min="0"
                      :max="field.type === 'Percentage' ? 100 : null"
                      :step="field.type === 'Percentage' ? 0.1 : 0.01"
                    >
                      <template #note>
                        <small v-if="field.default" class="text-muted">
                          Default Value: {{ field.default }}
                        </small>
                      </template>
                      <template v-if="field.type === 'Percentage'" #append>
                        <span class="input-group-text">%</span>
                      </template>
                      <template v-if="field.type === 'Amount'" #prepend>
                        <span class="input-group-text">$</span>
                      </template>
                    </form-text>
                  </div>
                </div>
              </template>
            </b-card>

            <b-card>
              <template #header>Mobile App</template>
              <div>
                <div class="mb-2">
                  <qrcode :value="appUrl" :options="{ width: 100, margin: 0 }"></qrcode>
                </div>

                <button class="btn btn-primary btn-lg" @click.prevent="copyToClipboard">
                  <font-awesome-icon :icon="['fal', 'cloud-download']" />
                  BOLT App Install Link
                </button>

                <div class="mt-1">
                  <small class="text-muted">
                    <font-awesome-icon :icon="['fal', 'paste']" />
                    <template v-if="copied"> Copied to clipboard</template>
                    <template v-else> Click to copy</template>
                  </small>
                </div>
              </div>
            </b-card>
          </div>
        </div>

        <div class="text-right">
          <button :disabled="form.notReady()" class="btn btn-primary">
            <font-awesome-icon v-if="form.busy" :icon="['fal', 'spinner']" spin />
            <font-awesome-icon v-else :icon="['fal', 'check']" />
            {{ crud.getAction() === "create" ? "Create" : "Save" }}
          </button>
        </div>
      </b-form>
    </b-tab>

    <b-tab
      v-if="crud.data.id && $can('update', crud, 'emergency-contacts')"
      title="Emergency Contacts"
    >
      <emergency-contacts :current-user="crud.data" />
    </b-tab>

    <template v-if="form.role === 'technician' && crud.data.id">
      <b-tab v-if="$can('update', crud, 'documents')" title="Documents">
        <documents :current-user="crud.data" />
      </b-tab>
      <b-tab v-if="$can('update', crud, 'work-areas')" title="Work Areas">
        <work-areas :current-user="crud.data" />
      </b-tab>
      <b-tab v-if="$can('update', crud, 'unavailabilities')" title="Unavailability">
        <unavailabilities :current-user="crud.data" />
      </b-tab>
    </template>

    <b-tab v-if="$can('update', crud, 'brandings') && crud.data.id" title="Branding">
      <branding :current-user="crud.data" />
    </b-tab>

    <b-tab v-if="$can('update', crud, 'enrolled-devices') && crud.data.id" title="Enrolled Devices">
      <enrolled-devices :current-user="crud.data" />
    </b-tab>

    <b-tab v-if="$can('update', crud, 'activity') && crud.data.id" title="Activity">
      <activity :current-user="crud.data" />
    </b-tab>
  </b-tabs>
</template>

<script type="text/babel">
import VueQrcode from "@chenfengyuan/vue-qrcode";
import externals from "../../constants/externals";
import FileUploader from "../../components/common/FileUploader.vue";
import Activity from "./partials/Activity.vue";
import Documents from "./partials/Documents.vue";
import EmergencyContacts from "./partials/EmergencyContacts.vue";
import Unavailabilities from "./partials/Unavailabilities.vue";
import EnrolledDevices from "./partials/EnrolledDevices.vue";
import WorkAreas from "./partials/WorkAreas.vue";
import Branding from "./partials/Branding.vue";
import format from "../../mixins/format";
import EventBus from "../../imports/event-bus";

export default {
  components: {
    EnrolledDevices,
    Activity,
    Documents,
    EmergencyContacts,
    FileUploader,
    Unavailabilities,
    WorkAreas,
    Branding,
    qrcode: VueQrcode,
  },
  mixins: [format],

  data() {
    return {
      initialised: false,
      allBrandData: [],
      copied: false,
      addressExpanded: false,
      statuses: ["Active", "Shadow", "Suspended", "Inactive"],
      profilePostAction: null,
      vanPostAction: null,

      profilePlaceholder:
        "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMgAAADICAAAAACIM/FCAAANGklEQVR4AezWgQbAMAyE4b3/a+16e62xDKAINyb+Y8Qk6qPaHmtIgHwVven+B/I7SHcg0DcJks8cSHfhXX9qCwEZA+FmBwIESOR02cwDARIPECBAgAABAgQIECD2ZZ2q0l56Sqmq+rTs8Cs5D7mbuQ/ttnElDMDv/1IbEdMgOU7ilt77lvTEtkjM/PeIkc7NeldekyYl/e5Uw3eAYYXFKZGYtZxExKtQi2OqSJVSop2HiJoKixKL5Wwqy2RrO0iyUAvdfUjbJiKi1Q8WYSahqkosqm3fMO98jRhXnGdG2YRIpjcPjo5PTo6PDm7OsqmK3KA8VaLdh6hlSRVbyodP3n+p0ZQAojRRf//z+cmMpzyZcM6685AJm5DeefjltABwxzLFA/Dm/OW9GYsJ7TwkM+0/+1KwSDgQ4e4eASwX+dm7Y2XdOYiJqBIpi5owMx2+OwcQuCTNxwfTJEKiqswsl9U+r36MDSESVRHNXCXS/ZOzBkBc4vC2aJpX+6RUsYguIrR9CAsvkplU91+XhSFwWSI8AvH1hKe5YhJhMeOtQ1Q5kSpXOecnDRr3luFYE2+/uQP4sC/T9rGsptuHCLNmE64OvgENAKw65dLUi6+nIksEbx0i3HZKkpcOd4R78csV7X0anNeA/3l78VBl2j5EWZQqvf3hDPh/T4RjTUqgzRyoHc35vekkKZNuHSIkxnryu68IEV4clyVKAdB+A348UlIm2xokiTKLirLOJgc1VollcCFrl5eXWjFn/llt2ncqR2+ImDKrSp4o3T896w0B6leabGKiZsKyeYgwkQgrVyc/AO8NaYCXmUUpqfEWIMzLbboc/EBB3RcSXoBnasJEzLyVfS1dRG5+g6N/jRSgRv2I2DKTSNo4hPnn4Mp/oQBNbwgccHw7npCwmG4BQsyUePocTYNA9IacFyDK15tSkYroFoaWqsjsADVQrtEjjqiBBq/vkFiqbJjZQR1iQkqyd9YVEBeCZebPf8tVNUsXm8IXsm55b4iSierbMhQkvt/cuzEV3jiEyFQOA0NBgHciLNQbIj2TLJm9x4CQ+a08Ed54sZMluV98MEigfFBLeeMQ4Zy/olkP6bo84PeVdTVuNgm5D5ThIA2aL2y0cQgb/4kBeyRQ0NwS3gTkb88+yXcAb9C14etqBgWoP9NFwPgQkdeB8OEgAfj3W5uHZKvdAR8Mskj9lPrO++oNkROUQAwGCTgQH3jjkMkfaIDhhpbDAZzuXRvCy1wVwvPSAGUwSGtBc2/jkINwR8RgEDgc8LfSM70hjx0Bx3CQggLEl43tomglQiryoQaAWA/pszwi9shYq+6zVntAVEjZvpRxILdJWUnHh1hSJuHZWavwGBSCiPsLBguPDlFSZuZbBXAgBoe8UiVhpvFrhIWF+BiIAgSGhvwpSqI9IJ2nULCwkjwYB4L4TEJioqNDmJmM5GkgfEjIqkt+kDArbwqizx0RgA8OOWcTkmrg45F/hXFm1pedGtohTRKr2Dr/91wvCLGNByHRSjYAYWEbE1KzSFLtCekQFjFifT4W5JyFSWX8kw9tj4g8GQvynYVZeTgIr30uUla+PxbkIymLkowOUUkilo7GgvyeFpC0CUglYtWdsSBvk7FIpaOfRdH2p9q5owkgMFQCEQicTElEhDYG+eZwBDAsBHFgpLopiIjq79EAHkNC4EC9x2wquqlTpqqPUfeBrK8ltJCvRmzCurkzjbc9EI4BIQGHv1ESJVIdHbLS6DfAHTEcxBExv6cskkhlYxB6HcCgPVIQ/mOqLEqsvDnIg2bgGmkQ5WsSktaysQs9aW9eAvNfGvjze98DKw9E8Qey8StWml8FgBgKUuDA933aPET2CxxlKIijBJ7z5iEs8hEFPhQEgRqzLczXYpV7DsdgEAf+Itk8RFXkG2I4CAInRszMm4WIcno8Hw7iaP6waaKNQ5Ky6nsfDNKgvpVEpDfkktmcl561IGa5ceLA8jN+yeVzUC4E4fAaKK8yqXCbrpo+05x4GWFS0tcID7hfaGiHyTQOFAAFHw+qpJxGgVw+75bYJrMvATjQHxI/MT5/RJUocy9InxN0q7DIxGxycgY0AcTFXBWCArgDz2dVVpL+cxp7GNpoZcTTxM/O4SXcu0JWgQeA8mlWUeYkMgLkckm+oTmRqL2JheTXodQJ8rNGPh8psXCPY3VmvuZ2hCjzRK2684cj0AVysdg9vj0gNUliwn0hvWtkMk1snCurjr5evMzRAdKmfiJcLSCcdBhIh+2ILDOdVHe/AB5rRtD6DWKsrgvPn1q65luCDgCZmFSHXxEFKPMS/xksUwPnNdA4mqfKVm0dwso36ORjoNQA3K8KWQ5Cx7fHe4mNhoX0SE48k8nBH0AAgF95aEW0X6d3k2Zm2/r/6pKYcSV7r2vEWQE61Egd8M93MnMilXEhV7nKq0JsmvLDrwACVx1aberXt4mVk1q1dUjmJKZiVXXwAfCrQzzw/Um+kYnUzNLWIcztJ4mJPTpFuerQquHvbjMrSZaKTbdfI6aTG6ySSLiavrr6gdWfh8rMrEKJRPm/VpWjQ4R1OlXmqZDuP/xwdcinh3vatlFnMyHZOiRzRWrCEz16ewo0V4XMgbPXh1qRGFcV29YhNyQLsdrdPwKl7jgXpfz5ULNMaKq/bR0ym1R7mY4/A6UBcHZVyMJcAvh0l3VKabp1SDW16uALylkAHl16xAPws4KvB5XN0n9BRn8nf0t3PgEOwIsH4qqQANqh2B5W3Um2dcjNtwUF8M7ntbwsl3qgeX1z8xATVk4solJVe/fqxoGIFaTbeS0A7fco9f2bKanJqrE6+gYxqbCpqKTEeutNaTwAXGh+Bwja7940r2+pMInqwsA0PsSURFjIhE6+AauW9oa0CQe+HrNmYhFiVR4dwlkSK3Ou8ss5lm2JC5A2V4TE6gfmL/MNZRVKojI6hJTZjJlvvQ1Es2pkb8jqFzSOeLOfWFSZmceHMIumiRx9AmqgtJ3h/4cA6AEJlPbpPh+u3idldIgIC1fTB/MGQFNWR6z9IavRVdonnN+fVaRMOjpEmUTz43MgigPugTa/QNAF8uuDPYCzp5mVyEaDtFUoRsSZ5VmD6BhcMdE8E1JiNkmqI0AWCmHVlJO8ajAeBM0rqTKpspgQDw4xZpWUxNheAOP1CBrghbJpqtrXHB6izMIkLAtHietC1i73hURYiYRZbXAImVDOIvy4YB4oY0EcUaM8YWUzEqPBISkLGVf67Bw1AIwEadPg/JlO2EhzNTiEjUgn8tBRClCPBmm7u6A8lIkS2wjFLhPOfDLHgjHq0PI50KA+4cyV6OAQTWZpv3ag98SAbleyzvcpaxqh2NXEvtXYFAT1VxNVHhzCbPQSiI1BgJekJINDjPiBN2g2BXHU/pDJBoeo7deIwFiQ1a9YpRSUfdXBITz7MwKlB6TD8r/VCIC/ZjQ4xJ7iDIjNQdwxxxMdHLLvQCBic5AIoNwc7EIPU+ZpsnzWYJFLAP8OvPSsyj+fMIBffymnZlUmJbo2xEiksvSidkRsDLJ6Rq9fJEuipNeGqJImvjWP5aHoWJDl43+FeLQbeCEjvX6NpD2SafXXagRfcuLtkn3idbdc8vglBPFnNZU0q64PmQnb4arLx4TExT+wiONAiafV9SeesZJ+WvV6bA4Sq9f8JKSi14YosT5EA4QDiBgNciH4CQMaPFTm60PIqukplkM2fHMQ958vWXA6rfT6NSKSni0Y+DV9JmH2TYE/1UquDVHe+1IjtgZxRPMtG18fUj1DARwXsjlI43gscm0I6ynQbA+CCMScrg+p7qFBAFuDFGCOY7o2xD6gOMr2IEBE+aLXhtytAw3814Z2gnQF/es/ATRxfG3I60Ag0GwL0gQi0LzpDVEm1cQz9wAQvr7B4y5HtPH9RJkp94AQ52T0YPXU24Y8qoyVpQck6XQysw/ADkAQ/pFnlQlxZ4iwWCUHTayyLQh+ftV3JJlwdwgbk0xeYFcg8XKiItYLkjJ/DiyzdchXNhaV7hBRsqMLJ623CfGjtnC7Q6os8iKwXPv6FiFA++15pfm37hDhPLEf8c89uDWJ1Y81j4hl1i1f9/hVCr5rZX2OEFV4v/adgTjmN4V7FTvpA5SdgQTiviWj7hAlfb9TELyzpNwZoiT2w2OHIP49E/faRblVdgpSym0h7QwR0kcBHxyySndI+GPhPhtEeRNodggCf8tXqxHmv/1RncHjH4BYZbwpHGskjrOKqTNExOZwXIQMf8zeAVKbcHeI3mkQuwUpB9oDIg8LArEzEBTE41418toR8J2pERTgnVAPyMfYJUigID5zn6F1hhayI0PL4Ygz0c4Q07JTkLKANJq7bxBPLr4RWBdIX/j6AB5oDrg75F4LiV2BRAs5lu6QxwWIsSEdgAtIedgD8sJ3DLKwvOgBee8IYGcgAAL+nrtDPgR8lyABwD9Sd8hX7BbEgYjvPSDfdwxSgIhzljX5H+9f5z8TkscoAAAAAElFTkSuQmCC",

      crud: null,
      form: new window.AppForm({
        status: "Active",
        has_webui_access: 0,
        core_compliance: 0,
        core_compliance_violations: [],
        profile_photo: "",
        profile_photo_time: "",
        van_photo: "",
        van_photo_time: "",
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        mobile: "",
        date_of_birth: "",
        start_date: "",
        inactive_reason: "",
        professional_accreditation_number: "",
        driver_license: "",
        vehicle_registration: "",
        company_name: "",
        abn: "",
        password: "",
        comments: "",
        role: "technician",
        brands: [],
        brand_single: null,
        service_types: [],
        type: null,
        contractor_job_commission: 0,
        contractor_booking_commission: 0,
        franchisee_job_commission: 0,
        franchisee_booking_commission: 0,
        admin_fee: 0,
        marketing_fee: 0,
        gmb_listing_url: "",
        supervisees: [],
        designation: null,
        designation_id: null,
        designation_data: {},
        addresses: [
          {
            id: null,
            street: null,
            city: null,
            state: null,
            postcode: null,
          },
        ],
      }),
    };
  },

  computed: {
    designationFields() {
      const fields = this.$lodash.get(this.form, "designation.fields");

      if (!fields) {
        return null;
      }

      return fields;
    },
    selectedBrandIds() {
      if (this.form.role === "technician") {
        return [this.form.brand_single];
      }

      return this.form.brands;
    },
    appUrl() {
      return externals.mobileApp.url;
    },
  },

  watch: {
    "form.designation"(designation) {
      this.form.designation_data = {};

      if (designation && designation.fields) {
        designation.fields.forEach((field) => {
          const slug = field.slug;
          let value = this.$lodash.get(this.crud.data, `designation_data.${slug}`);
          if (value === null) {
            value = field.default;
          }
          this.form.designation_data[slug] = value;
        });
      }
    },

    // watch form.status
    "form.status"(status) {
      if (this.crud.data.status !== "Inactive" && status === "Inactive") {
        window
          .sweetAlert({
            title: "Inactive reason",
            text: "Please provide a reason for inactivating this user",
            content: {
              element: "input",
              attributes: {
                placeholder: "Inactive reason?",
                type: "text",
              },
            },
            icon: "warning",
            dangerMode: true,
            buttons: {
              confirm: "Ok",
            },
          })
          .then((reason) => {
            if (!reason) {
              this.form.status = this.crud.data.status;
              window.toastr.error("Inactive reason is required, status not changed.");
              return;
            }

            this.form.inactive_reason = reason;
            window.toastr.info(
              "Inactive reason saved. Don't forget to click save to update the user status."
            );
          });
      }
    },
  },

  created() {
    this.init();
  },

  methods: {
    async init() {
      this.crud = new window.AppCrud("user", [
        "addresses",
        "registeredBy",
        "brands",
        "serviceTypes",
        "supervisees",
        "coreComplianceViolations",
        "designation.fields",
      ]);
      this.allBrandData = await this.$store.dispatch("brands/getData");

      this.profilePostAction = `${process.env.VUE_APP_API}/users/upload/profile/`;
      this.vanPostAction = `${process.env.VUE_APP_API}/users/upload/van/`;

      this.crud.initFormData(this.form).then(() => {
        this.form.has_webui_access = this.form.has_webui_access === 1;
        this.form.core_compliance = this.form.core_compliance === 1;

        // Init brands
        let brandIds = [];
        this.form.brands.forEach((brand) => {
          brandIds = brandIds.concat(brand.id);
        });
        this.form.brands = brandIds;
        this.form.brand_single = null;
        if (brandIds.length) {
          this.form.brand_single = brandIds[0];
        }

        // Init Addresses
        if (this.form.addresses && this.form.addresses.length === 0) {
          this.form.addresses = [
            {
              id: null,
              street: null,
              city: null,
              state: null,
              postcode: null,
            },
          ];
        }

        // Init Service Types
        if (this.crud.data.service_types && this.crud.data.service_types.length > 0) {
          this.form.service_types = this.crud.data.service_types;
        }

        // Init coreComplianceViolations
        this.form.core_compliance_violations = this.$lodash.map(
          this.form.core_compliance_violations,
          "violation_reason"
        );

        // Browser Title
        if (this.$lodash.get(this.crud, "data.name")) {
          // noinspection JSUnresolvedFunction
          this.$root.setTitle(`User - ${this.$lodash.get(this.crud, "data.name")}`);
        } else if (this.$lodash.get(this.crud, "data.id")) {
          // noinspection JSUnresolvedFunction
          this.$root.setTitle(`User - ${this.$lodash.get(this.crud, "data.id")}`);
        }

        this.initialised = true;
      });

      if (this.crud.getAction() === "create") {
        this.initialised = true;
      }
    },

    updateDesignation(designation) {
      this.form.designation_id = designation.id;
    },

    onSubmit() {
      this.form.core_compliance_violations = this.form.core_compliance
        ? []
        : this.form.core_compliance_violations;

      this.crud.submit(this.form).then((response) => {
        EventBus.$emit("user-updated");

        if (this.crud.getAction() === "create") {
          window.toastr.success("User created!", "Success");
          this.$router.push(
            {
              name: "users.edit",
              params: { id: response.id },
            },
            () => {
              this.init();
            }
          );
        } else {
          this.init();
          window.toastr.success("User updated!", "Success");
        }
      });
    },

    updateSupervisees(users) {
      this.form.supervisees = users;
    },

    copyToClipboard() {
      window.toastr.success("Copied BOLT App download URL to clipboard.", "Success");
      window.navigator.clipboard.writeText(
        `URL: ${externals.mobileApp.url}\n` + "Access Code: 072022"
      );
      this.copied = true;
    },

    setProfilePhoto(url) {
      this.form.profile_photo = url;
    },

    setVanPhoto(url) {
      this.form.van_photo = url;
    },

    updateFormBrands() {
      this.form.brands = [this.form.brand_single];
    },
  },
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
::v-deep .file-uploader__preview {
  label {
    max-width: 100%;
    max-height: 100px;
    width: 100%;
    height: 100px;
    text-align: center;

    img {
      height: 100px;
    }
  }
}

::v-deep .file-uploader__main,
::v-deep .file-uploader__edit-buttons {
  text-align: center;
}

::v-deep .profile-card .file-uploader__preview img {
  border-radius: 50%;
}

.user-general-card input#role[disabled="disabled"] {
  text-transform: capitalize;
}
</style>
